import { Box, Typography } from '@mui/material';

import { CustomTextField } from '../../../../../../components/CustomInput';
import {
  ColoredCircularProgress,
  WhiteCircularProgress,
} from '../../../../../../components/UI/Typography';
import { Constants } from '../../../../../../constants/hiveNotices';
import { IconOpenInNewMS } from '../../../../../../constants/icons';
import { youtubeEmbedUrl } from '../../../../../../helpers/youtube';
import { TableSection } from '../../NoticeDetails/styles';
import ConfirmationDeleteDialog from '../ConfirmationDeleteDialog';
import {
  Actions,
  EmptyState,
  FilledButton,
  FormRow,
  HelpText,
  OutlinedButton,
  PlayerFrame,
  PlayerWrapper,
  PreviewGrid,
  RemoveRow,
  SavedLink,
  UnsupportedLink,
} from './styles';
import { usePresentationVideo } from './usePresentationVideo';

const PLAYER_PERMISSIONS = [
  'accelerometer',
  'autoplay',
  'clipboard-write',
  'encrypted-media',
  'gyroscope',
  'picture-in-picture',
  'web-share',
].join('; ');

interface PresentationVideoSectionProps {
  noticeId: number;
  initialUrl: string | null;
  onSaved: (url: string | null) => void;
}

export function PresentationVideoSection({
  noticeId,
  initialUrl,
  onSaved,
}: PresentationVideoSectionProps): JSX.Element {
  const {
    url,
    setUrl,
    savedUrl,
    savedVideoId,
    editing,
    startEditing,
    cancelEditing,
    canSave,
    loading,
    handleSave,
    handleRemove,
  } = usePresentationVideo({ noticeId, initialUrl, onSaved });

  return (
    <TableSection>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography fontSize={21}>
          {Constants.presentationVideoTitle}
        </Typography>
        {savedUrl && !editing && (
          <OutlinedButton onClick={startEditing} disabled={loading}>
            {Constants.editVideo}
          </OutlinedButton>
        )}
      </Box>
      <HelpText>{Constants.presentationVideoHelp}</HelpText>

      {editing && (
        <FormRow>
          <CustomTextField
            id="presentation-video-url"
            label={Constants.youtubeLinkLabel}
            value={url}
            setValue={setUrl}
            maxLength={200}
            disabled={loading}
          />
          <Actions>
            <FilledButton onClick={handleSave} disabled={!canSave}>
              {loading ? (
                <WhiteCircularProgress size={22} />
              ) : (
                Constants.saveVideo
              )}
            </FilledButton>
            {savedUrl && (
              <OutlinedButton onClick={cancelEditing} disabled={loading}>
                {Constants.cancelVideoEdit}
              </OutlinedButton>
            )}
          </Actions>
        </FormRow>
      )}

      {savedUrl ? (
        <PreviewGrid>
          {savedVideoId ? (
            <PlayerWrapper>
              <PlayerFrame
                src={youtubeEmbedUrl(savedVideoId)}
                title={Constants.presentationVideoFrameTitle}
                allow={PLAYER_PERMISSIONS}
                allowFullScreen
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </PlayerWrapper>
          ) : (
            <UnsupportedLink>{Constants.unsupportedVideoLink}</UnsupportedLink>
          )}
          <RemoveRow>
            <SavedLink
              href={savedUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {savedUrl}
              {IconOpenInNewMS}
            </SavedLink>
            {loading ? (
              <ColoredCircularProgress size={22} />
            ) : (
              <ConfirmationDeleteDialog
                iconDelete={false}
                tooltip={Constants.removeVideo}
                text={Constants.removeVideoConfirm}
                modalCallback={handleRemove}
              />
            )}
          </RemoveRow>
        </PreviewGrid>
      ) : (
        <EmptyState>{Constants.noVideo}</EmptyState>
      )}
    </TableSection>
  );
}
